// Pure predicate helper for ReactTable's client-side filter pass.
// Mirrors a narrow subset of the backend FilterSpecificationBuilder operators:
// contains, doesnotcontain, equals, note, startswith, endswith, isanyof,
// isempty, isnotempty, >, <, >=, <=, after, before, onorafter, onorbefore.
// Type mismatches return false (row filtered out) rather than throwing.

// Normalizes a value so snake_case, spaces and mixed case are treated
// equivalently when matching. E.g. "PENDING_IMPACT_ANALYSIS", "Pending Impact
// Analysis" and "pending impact analysis" all normalize to "pending impact analysis".
const normalizeText = (v) =>
  v == null ? "" : String(v).trim().toLowerCase().replace(/_+/g, " ").replace(/\s+/g, " ");

const asNumber = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : NaN;
};

const asDate = (v) => {
  if (v == null || v === "") return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};

const compareNumbers = (a, b) => {
  const na = asNumber(a);
  const nb = asNumber(b);
  if (Number.isNaN(na) || Number.isNaN(nb)) return null;
  return na - nb;
};

const compareDates = (a, b) => {
  const da = asDate(a);
  const db = asDate(b);
  if (da == null || db == null) return null;
  return da.getTime() - db.getTime();
};

// Reads a nested field path like "ingredient.name" off a row.
const readField = (row, field) => {
  if (!field || !field.includes(".")) return row == null ? undefined : row[field];
  return field.split(".").reduce((acc, key) => (acc == null ? acc : acc[key]), row);
};

export function applyClientFilter(row, item) {
  if (!item || !item.field || !item.operator) return true;
  const operator = String(item.operator).toLowerCase();
  const value = item.value;
  const current = readField(row, item.field);

  try {
    switch (operator) {
      case "contains":
        return value == null ? current == null : normalizeText(current).includes(normalizeText(value));
      case "doesnotcontain":
        return value == null ? current != null : !normalizeText(current).includes(normalizeText(value));
      case "equals":
      case "is":
      case "=":
        return normalizeText(current) === normalizeText(value);
      case "not":
      case "!=":
      case "doesnotequal":
        return normalizeText(current) !== normalizeText(value);
      case "startswith":
        return normalizeText(current).startsWith(normalizeText(value));
      case "endswith":
        return normalizeText(current).endsWith(normalizeText(value));
      case "isanyof": {
        if (value == null || value === "") return true;
        const selected = String(value).split(",").map((v) => v.trim()).filter(Boolean);
        return selected.map(normalizeText).includes(normalizeText(current));
      }
      case "isempty":
        return current == null || current === "";
      case "isnotempty":
        return current != null && current !== "";
      case ">": {
        const c = compareNumbers(current, value);
        return c == null ? false : c > 0;
      }
      case "<": {
        const c = compareNumbers(current, value);
        return c == null ? false : c < 0;
      }
      case ">=":
      case "onorafter": {
        const c = operator === ">=" ? compareNumbers(current, value) : compareDates(current, value);
        return c == null ? false : c >= 0;
      }
      case "<=":
      case "onorbefore": {
        const c = operator === "<=" ? compareNumbers(current, value) : compareDates(current, value);
        return c == null ? false : c <= 0;
      }
      case "after": {
        const c = compareDates(current, value);
        return c == null ? false : c > 0;
      }
      case "before": {
        const c = compareDates(current, value);
        return c == null ? false : c < 0;
      }
      default:
        return false;
    }
  } catch {
    return false;
  }
}

export default applyClientFilter;
